import { Content, Page } from '@backstage/core-components';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubLink,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { useContext, useState } from 'react';
import { MyNews } from './MyNews';
import DevHubSupportButton from '../common/DevHubSupportButton';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { MyTeams } from './MyTeams';
import { MyTools } from './MyTools';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { MyWork } from './MyWork';
import { MyWorklistView } from './MyWorklistView';
import {
  ApplicationFeedbackButton,
  DevhubFeedbackProps,
} from '../insights/feedback/ApplicationFeedbackButton';
import { Navigate } from 'react-router-dom';
import LogoFull from '../../../components/Root/LogoFullLight';
import { ExecutiveAppView } from './ExecutivePersona/ExecutiveAppView';
import { NewAnnouncementBanner } from '@backstage-community/plugin-announcements';
import { UserContext } from '../../contexts/UserContext';
import {
  MyIdeas,
  ExperimentalOnboardingHero,
} from '../onboarding/experimentation';

/**
 * The primary DevHub home page component.
 * @constructor
 */
export const DevHubHomePage = () => {
  const { isExecutive } = useContext(UserContext);
  const [viewMode, setViewMode] = useState<'default' | 'executive'>('default');
  const isMyWorkEnabled = localStorage.getItem('my-work-flag') !== 'false';
  const isMyToolsEnabled = localStorage.getItem('my-tools-flag') !== 'false';
  const isMyTeamsEnabled = localStorage.getItem('my-teams-flag') !== 'false';
  const isMyNewsEnabled = localStorage.getItem('my-news-flag') !== 'false';
  const isMyIdeasEnabled = localStorage.getItem('my-ideas-flag') !== 'false';
  const allFlagsFalse =
    !isMyWorkEnabled &&
    !isMyTeamsEnabled &&
    !isMyNewsEnabled &&
    !isMyToolsEnabled &&
    !isMyIdeasEnabled;

  const devHubFeedbackProps: DevhubFeedbackProps = {
    applicationId: 59712,
    name: 'DevHub',
  };

  if (ConfigHelper.instance.isFeatureEnabled(Feature.DEVHUB_PUBLIC_HOMEPAGE)) {
    return <Navigate to="/api-portals/connected-living" />;
  }

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <DevHubGrid container>
            <DevHubGrid item md={12}>
              <NewAnnouncementBanner category="devhub" />
            </DevHubGrid>
          </DevHubGrid>
          <DevHubGrid
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
          >
            <ConfigFeatureCheck feature={Feature.EXECUTIVE_PERSONA}>
              {isExecutive && (
                <DevHubGrid item>
                  {viewMode === 'executive' ? (
                    <DevHubButton
                      variant="outlined"
                      color="primary"
                      onClick={() => setViewMode('default')}
                      startIcon={<DevHubDynamicIcon iconName="ArrowBackIos" />}
                    >
                      Back to Developer View
                    </DevHubButton>
                  ) : (
                    <DevHubButton
                      variant="contained"
                      color="primary"
                      onClick={() => setViewMode('executive')}
                      startIcon={<DevHubDynamicIcon iconName="People" />}
                    >
                      Executive View
                    </DevHubButton>
                  )}
                </DevHubGrid>
              )}
            </ConfigFeatureCheck>
            <ConfigFeatureCheck feature={Feature.FEEDBACK}>
              <ApplicationFeedbackButton application={devHubFeedbackProps} />
            </ConfigFeatureCheck>
            <DevHubSupportButton />
          </DevHubGrid>
          <DevHubBox padding="medium" />
          <ConfigFeatureCheck feature={Feature.EXPERIMENTATION_ONBOARDING}>
            <ExperimentalOnboardingHero />
          </ConfigFeatureCheck>
          {viewMode === 'executive' ? (
            <ConfigFeatureCheck feature={Feature.EXECUTIVE_PERSONA}>
              <DevHubGrid container item xs={12}>
                <DevHubGrid item xs={12}>
                  <ExecutiveAppView />
                </DevHubGrid>
                <DevHubGrid item xs={4}>
                  <MyTeams />
                </DevHubGrid>
                <DevHubGrid item xs={8}>
                  <MyWorklistView />
                </DevHubGrid>
              </DevHubGrid>
            </ConfigFeatureCheck>
          ) : (
            <DevHubGrid container justifyContent="center" spacing={3}>
              {isMyWorkEnabled && (
                <DevHubGrid container item xs={12}>
                  <DevHubGrid item xs={12}>
                    <MyWork />
                  </DevHubGrid>
                </DevHubGrid>
              )}
              <DevHubGrid container item xs={12}>
                <ConfigFeatureCheck
                  feature={Feature.EXPERIMENTATION_ONBOARDING}
                >
                  {isMyIdeasEnabled && (
                    <DevHubGrid item xs={12} lg={4}>
                      <MyIdeas />
                    </DevHubGrid>
                  )}
                </ConfigFeatureCheck>
                {isMyToolsEnabled && (
                  <DevHubGrid item xs={12} lg={4}>
                    <MyTools />
                  </DevHubGrid>
                )}
                <ConfigFeatureCheck feature={Feature.DEVXPROTECT_EXPERIENCE}>
                  {isMyTeamsEnabled && (
                    <DevHubGrid item xs={12} lg={4}>
                      <MyTeams />
                    </DevHubGrid>
                  )}
                </ConfigFeatureCheck>
                {isMyNewsEnabled && (
                  <DevHubGrid item xs={12} lg={4}>
                    <MyNews />
                  </DevHubGrid>
                )}
                {allFlagsFalse && (
                  <>
                    <DevHubGrid
                      container
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    >
                      <DevHubGrid item>
                        <LogoFull height="250px" />
                      </DevHubGrid>
                      <DevHubGrid item>
                        <DevHubAlert severity="info">
                          <DevHubTypography fontSize="large">
                            It appears that all homepage widgets have been
                            disabled. Please navigate to{' '}
                            <DevHubLink href="/settings">Settings</DevHubLink>{' '}
                            to enable these features.
                          </DevHubTypography>
                        </DevHubAlert>
                      </DevHubGrid>
                    </DevHubGrid>
                  </>
                )}
              </DevHubGrid>
            </DevHubGrid>
          )}
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
